/**
 * Composable that builds the stepper UI state from a ticket payload.
 *
 * Replaces the ~180-line stepper-building algorithm that was duplicated
 * across 5 detail views. The pure building blocks live in
 * use-ticket-stepper-helpers.js; this file glues them with reactive
 * state.
 *
 * Usage in a view:
 *   const {
 *     recordsSteps,
 *     stepperProps,
 *     headerStateData,
 *     ticketStateId,
 *     lastStateTag,
 *     rebuild
 *   } = useTicketStepper(initialTicketData, {
 *     onCancel: () => stepperRef.value?.cancelStep(),
 *     onFinish: () => stepperRef.value?.finishStep()
 *   });
 *
 *   const stepperData = async (id, prefetched) => {
 *     const ticketData =
 *       prefetched ?? (await store.dispatch(FETCH_TICKET_ID, id)).data;
 *     rebuild(ticketData);
 *   };
 *
 * The composable returns the four canonical fields listed in issue #10
 * (`recordsSteps`, `stepperProps`, `headerStateData`, `ticketStateId`)
 * plus two extras used by views to derive view-specific booleans:
 *   - `lastStateTag` (ref) — raw backend tag, e.g. 'En aseguradora'
 *   - `rebuild(data)` — function to re-run the algorithm on new data
 */

import { reactive, ref } from 'vue';
import {
  buildFinalObj,
  buildHeaderStateData,
  buildRecordsSteps,
  computeCurrentStep,
  formatDates,
  getLastStateId,
  getLastStateTag,
  moveCancelToPenultimate,
  removeFutureStates
} from './use-ticket-stepper-helpers';

export function useTicketStepper(ticketData = null, options = {}) {
  const {
    onCancel = () => {},
    onFinish = () => {},
    pruneFutureStates = true
  } = options;

  const recordsSteps = reactive([]);
  const stepperProps = reactive({ state: '', dates: {} });
  const headerStateData = reactive({
    text: '',
    color: 'default',
    cancel_disabled: true,
    revisado_disabled: true,
    show_buttons: false,
    showCancelAndExecuted: false
  });
  const ticketStateId = ref('');
  const lastStateTag = ref('');

  function rebuild(data) {
    if (!data) return;

    // 1. History rows for the timeline.
    const nextRecords = buildRecordsSteps(data.states);
    recordsSteps.splice(0, recordsSteps.length, ...nextRecords);

    // 2. Last state id (used by views to gate action buttons).
    ticketStateId.value = getLastStateId(data.states);

    // 3. Raw last tag — exposed so views can derive booleans like
    //    `reviewIncorporation` (incorporation) or `finishAlta` (massive).
    lastStateTag.value = getLastStateTag(data.states);

    // 4. Compute which step to highlight. If the ticket is Cancelado,
    //    the step is "the last good state" and we also fire onCancel
    //    so the stepper component can mark an X on that step.
    const step = computeCurrentStep(data.states);
    if (lastStateTag.value === 'Cancelado') onCancel();
    if (lastStateTag.value === 'Ejecutado') onFinish();
    stepperProps.state = step;

    // 5. Build the per-step date map, fold the cancel date if needed,
    //    drop future steps (handles ticket rollbacks), and format.
    //    `pruneFutureStates` is opt-out because the massive-modification
    //    view intentionally keeps stale future dates (it switches on a
    //    never-set variable, so the original code never deletes them).
    let finalObj = buildFinalObj(data.states);
    finalObj = moveCancelToPenultimate(finalObj, lastStateTag.value);
    if (pruneFutureStates) {
      removeFutureStates(finalObj, step);
    }
    finalObj = formatDates(finalObj);
    stepperProps.dates = finalObj;

    // 6. Page-header state.
    Object.assign(headerStateData, buildHeaderStateData(lastStateTag.value));
  }

  if (ticketData) {
    rebuild(ticketData);
  }

  return {
    recordsSteps,
    stepperProps,
    headerStateData,
    ticketStateId,
    lastStateTag,
    rebuild
  };
}
